import { isAssetOnboardingRoute } from './common-utils';

/**
 * Post-authentication redirect, carried as `?redirect=<token>` on the sign-in / sign-up URL.
 *
 * A short token rather than a full URL. The value is visible in the address bar and survives
 * the hop from sign-in to sign-up, and because only the routes listed here can be produced,
 * a hand-edited `?redirect=` cannot send a freshly authenticated user to an arbitrary
 * destination — anything unrecognised falls through to the caller's own default.
 */
export const AUTH_REDIRECT_ROUTES: Readonly<Record<string, string>> = {
    deposit: '/deposit-asset',
    'my-deposits': '/my-deposits'
};

/**
 * The token to put on the auth URL when a guard turns a user away from `url`.
 *
 * @returns the token, or null when the route has no post-login destination of its own.
 */
export function authRedirectTokenFor(url: string): string | null {
    const path = String(url || '').split('?')[0].split('#')[0];

    // `/onboardassets` loads the deposit flow too, so it maps to the same token.
    if (isAssetOnboardingRoute(path)) return 'deposit';

    const match = Object.entries(AUTH_REDIRECT_ROUTES).find(([, route]) => route === path);
    return match ? match[0] : null;
}

/**
 * Resolve a `?redirect=` token to the route it stands for.
 *
 * @returns the route, or null when the param is absent or not one we issue.
 */
export function resolveAuthRedirect(token: string | null | undefined): string | null {
    if (!token) return null;
    return AUTH_REDIRECT_ROUTES[token] ?? null;
}

/**
 * Top-level routes that make up signing in / signing up. Entering one of these from anywhere
 * else is what captures the post-login return page, and none of them is ever a page to return to.
 */
const AUTH_FLOW_SEGMENTS: ReadonlySet<string> = new Set([
    'sign-in',
    'sign-up',
    'forgot-password',
    'reset-password',
    'verify-email',
    'confirm-email',
    'borrow-register',
    'lend-register'
]);

/**
 * Hand-off pages, not somewhere the user was browsing: the KYC provider's callback and the
 * email-footer unsubscribe page.
 */
const NON_RETURNABLE_SEGMENTS: ReadonlySet<string> = new Set(['kyc-verify', 'unsubscribe']);

function topLevelSegment(url: string): string {
    return String(url || '').split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? '';
}

/** Whether `url` is one of the sign-in / sign-up pages. */
export function isAuthFlowUrl(url: string | null | undefined): boolean {
    return !!url && AUTH_FLOW_SEGMENTS.has(topLevelSegment(url));
}

/**
 * Whether `url` carries a one-time `token` query param — the email-verification link
 * (`/dashboard?id=…&token=…`) or a reset link. Replaying one after login is never useful.
 */
function carriesToken(url: string): boolean {
    const query = url.split('#')[0].split('?')[1];
    return !!query && new URLSearchParams(query).has('token');
}

/**
 * Whether `url` is safe to send a freshly authenticated user to: an in-app path (one leading
 * slash, so never `//host` or an absolute URL), not the bare root, not an auth page —
 * returning there would just bounce through the auth guard — and not a one-time token link.
 */
export function isReturnableUrl(url: string | null | undefined): url is string {
    if (!url || !/^\/(?![/\\])/.test(url)) return false;
    const segment = topLevelSegment(url);
    return segment !== ''
        && !NON_RETURNABLE_SEGMENTS.has(segment)
        && !isAuthFlowUrl(url)
        && !carriesToken(url);
}
